import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Briefcase,
  Settings,
  LogOut,
  Bell,
  ArrowLeft,
  Search,
  Bookmark,
  Building,
  MapPin,
  Clock,
  DollarSign,
  Eye,
  Star,
  Navigation,
  Trash2,
  TrendingUp,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useSavedJobsQuery, useRemoveSavedJob } from '../../hooks/useCandidateData';
import type { SavedJob } from '../../api/savedJobs';
import { toast } from 'sonner';

export function SavedJobs() {
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('saved');
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: savedJobs = [],
    isPending,
    isError,
    refetch,
  } = useSavedJobsQuery();
  const removeMutation = useRemoveSavedJob();

  const filteredJobs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return savedJobs
      .filter((job) => (filterType === 'all' ? true : job.type?.toLowerCase().includes(filterType)))
      .filter(
        (job) =>
          !normalizedSearch ||
          job.title.toLowerCase().includes(normalizedSearch) ||
          job.company.toLowerCase().includes(normalizedSearch),
      )
      .sort((a, b) => {
        if (sortBy === 'match') {
          return (b.matchScore ?? 0) - (a.matchScore ?? 0);
        }
        if (sortBy === 'salary') {
          const salaryA = parseInt((a.salary ?? '').replace(/[^0-9]/g, ''), 10) || 0;
          const salaryB = parseInt((b.salary ?? '').replace(/[^0-9]/g, ''), 10) || 0;
          return salaryB - salaryA;
        }
        const dateA = new Date(a.savedDate ?? 0).getTime();
        const dateB = new Date(b.savedDate ?? 0).getTime();
        return dateB - dateA;
      });
  }, [savedJobs, filterType, sortBy, searchTerm]);

  const stats = useMemo(
    () => [
      { label: 'Saved Jobs', value: savedJobs.length },
      { label: 'High Match', value: savedJobs.filter((job) => (job.matchScore ?? 0) >= 85).length },
      { label: 'Urgent Applications', value: savedJobs.filter((job) => job.urgent).length },
    ],
    [savedJobs],
  );

  const handleRemove = (jobId: string | number) => {
    removeMutation
      .mutateAsync(jobId)
      .then(() => toast.success('Job removed from saved list'))
      .catch((error) => toast.error(error instanceof Error ? error.message : 'Unable to remove job right now'));
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">Lighthouse Jobs</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/life">
                <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                  Life Platform
                </Button>
              </Link>
              <Link to="/candidate/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="icon" className="text-white">
                  <LogOut className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/candidate/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white mb-4 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="mb-2 text-gold-gradient">Saved Jobs</h1>
          <p className="text-gray-400">Jobs you&apos;ve bookmarked for later review</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {isPending
            ? Array.from({ length: 3 }).map((_, index) => (
                <Card key={`saved-stats-skeleton-${index}`} className="bg-white/5 border-white/10">
                  <CardContent className="p-6 space-y-3">
                    <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
                    <div className="h-6 w-14 animate-pulse rounded bg-white/10" />
                  </CardContent>
                </Card>
              ))
            : stats.map((stat) => (
                <Card key={stat.label} className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <div className="text-white mb-1">{stat.value}</div>
                    <p className="text-sm text-gray-400">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-6 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
            <div className="flex-1 relative md:mr-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search saved jobs..."
                className="pl-10 bg-white/10 border-white/20 text-white"
              />
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="full">Full-time</SelectItem>
                  <SelectItem value="part">Part-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48 bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Recently Saved" />
                </SelectTrigger>
                <SelectContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl">
                  <SelectItem value="saved">Recently Saved</SelectItem>
                  <SelectItem value="match">Highest Match</SelectItem>
                  <SelectItem value="salary">Highest Salary</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Saved jobs */}
        <div className="space-y-4">
          {isPending ? (
            <SavedJobsSkeleton />
          ) : (
            filteredJobs.map((job) => (
              <SavedJobCard key={job.id} job={job} onRemove={handleRemove} removing={removeMutation.isPending} />
            ))
          )}

          {filteredJobs.length === 0 && !isPending && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-12 text-center">
                <Bookmark className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-white mb-2">No saved jobs</h3>
                <p className="text-gray-400">Save roles from the job board to keep track of them here.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {isError && (
          <Card className="bg-red-500/10 border border-red-500/40 mt-6">
            <CardContent className="p-4 flex items-center justify-between text-sm text-red-100">
              <span>We couldn&apos;t load your saved jobs. Please try again.</span>
              <Button variant="outline" size="sm" className="border-red-300 text-red-200" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SavedJobCard({
  job,
  onRemove,
  removing,
}: {
  job: SavedJob;
  onRemove: (id: string | number) => void;
  removing: boolean;
}) {
  return (
    <Card className="bg-white/5 border border-white/10 overflow-hidden">
      <CardContent className="p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Building className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="text-white">{job.title}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                  <span>{job.company}</span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {job.type}
                  </span>
                  {job.commute && (
                    <span className="flex items-center gap-1">
                      <Navigation className="w-4 h-4" />
                      {job.commute}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className="text-[#ba9036] mb-3">{job.salary}</p>
            <p className="text-sm text-gray-300 mb-4">{job.description}</p>
            <div className="flex flex-wrap gap-2">
              {(job.requirements ?? []).slice(0, 4).map((req) => (
                <Badge key={req} variant="outline" className="border-white/20 text-gray-300">
                  {req}
                </Badge>
              ))}
            </div>
          </div>
          <div className="w-full md:w-auto space-y-3">
            <div className="text-center">
              <p className="text-sm text-gray-400">Match Score</p>
              <p className="text-3xl text-white">{job.matchScore ?? '—'}%</p>
              {job.matchScore && job.matchScore >= 85 && (
                <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/50">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  High match
                </Badge>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/5">
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </Button>
              <Button size="sm" className="bg-gold-gradient text-black hover:brightness-110">
                Apply Now
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="bg-red-500/20 border-red-500/40 text-red-200 hover:bg-red-500/30"
                disabled={removing}
                onClick={() => onRemove(job.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                View Match Details
              </Button>
            </DialogTrigger>
            <DialogContent className="role-surface-candidate border-white/10 bg-role-candidate-overlay text-white backdrop-blur-xl max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">{job.title}</DialogTitle>
                <DialogDescription className="text-gray-400">{job.company}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-gray-300">
                {(job.benefits ?? []).map((benefit) => (
                  <p key={benefit}>• {benefit}</p>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
            <DollarSign className="w-4 h-4 mr-2" />
            {job.savedDate ? `Saved on ${job.savedDate}` : 'Recently saved'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SavedJobsSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={`saved-job-skeleton-${index}`} className="bg-white/5 border border-white/10 overflow-hidden">
          <CardContent className="p-6 space-y-4">
            <div className="h-6 w-1/3 animate-pulse rounded bg-white/10" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-white/10" />
            <div className="h-14 w-full animate-pulse rounded bg-white/10" />
          </CardContent>
        </Card>
      ))}
    </>
  );
}
